/**
 * The sleep timer: a pure state machine, no React and no <audio> element in
 * sight, in the same shape as `player.ts` (a class, a listener set, a single
 * `patch`-style setter).
 *
 * It never touches playback itself. It only ever signals: the caller reads
 * `state.mode` going to "off" as "stop now", and `fadeVolume(state.remaining)`
 * as the multiplier to apply to whatever volume it already had. That split
 * exists so this file stays testable without a DOM, and so nobody has to
 * wonder whether a menu re-render silently paused a book.
 *
 * Two modes ("end of chapter" and "N chapters") have no wall-clock deadline
 * of their own: only the player knows when a chapter ends, so they wait for
 * `update()` to be fed the current time and the current chapter's end. A
 * plain duration needs none of that and ticks on its own clock instead,
 * because a 30 minute countdown must not drift or stall just because the
 * player was paused and stopped calling update().
 */

export const SLEEP_DURATIONS = [5, 10, 15, 30, 45, 60] as const;
export type SleepDurationMinutes = (typeof SLEEP_DURATIONS)[number];

export const SLEEP_CHAPTER_COUNTS = [1, 2, 3] as const;
export type SleepChapterCount = (typeof SLEEP_CHAPTER_COUNTS)[number];

/** A choice made in the menu. */
export type SleepPick =
  | { kind: "off" }
  | { kind: "duration"; minutes: number }
  | { kind: "endOfChapter" }
  | { kind: "chapters"; count: SleepChapterCount };

/** What is currently armed, carried in `SleepState.mode`. */
export type SleepMode =
  | { kind: "off" }
  | { kind: "duration"; minutes: number }
  | { kind: "endOfChapter" }
  | { kind: "chapters"; total: number; left: number };

export interface SleepState {
  mode: SleepMode;
  /**
   * Seconds until playback should stop, or null while that is not yet known:
   * either the timer is off, or it is a "chapters" countdown that has not
   * reached its last chapter, so nobody can say yet how long that is.
   */
  remaining: number | null;
  /** True inside the last FADE_SECONDS of a known countdown. */
  fading: boolean;
}

type Listener = (state: SleepState) => void;
type WarningListener = () => void;

/** The fade-out window, and the point past which "remaining" is a plain number. */
const FADE_SECONDS = 20;
/** When the one-time "warning" event fires, seconds before the cutoff. */
const WARNING_SECONDS = 60;
/** How long after a cutoff `extend()` still works: the "I wasn't asleep yet" window. */
const EXTEND_GRACE_MS = 60_000;

const OFF_STATE: SleepState = { mode: { kind: "off" }, remaining: null, fading: false };

/**
 * Volume multiplier for a given `remaining`, 1 outside the fade window. Pure,
 * so the fade curve is testable without instantiating a timer, and reusable
 * by anything that only has the number (e.g. a snapshot read from state).
 */
export function fadeVolume(remaining: number | null): number {
  if (remaining === null || remaining >= FADE_SECONDS) return 1;
  if (remaining <= 0) return 0;
  return remaining / FADE_SECONDS;
}

export class SleepTimer {
  private state: SleepState = OFF_STATE;
  private listeners = new Set<Listener>();
  private warningListeners = new Set<WarningListener>();
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private warned = false;
  /** Set the moment a countdown reaches zero; clears extend()'s grace window. */
  private endedAt: number | null = null;
  /** Null until the first update(), so the very first call cannot look like a chapter change. */
  private lastChapterIndex: number | null = null;

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  /** Fires once per countdown, at WARNING_SECONDS left, so the UI can warn the reader. */
  onWarning(listener: WarningListener): () => void {
    this.warningListeners.add(listener);
    return () => this.warningListeners.delete(listener);
  }

  get snapshot(): SleepState {
    return this.state;
  }

  /** Apply a choice from the menu. "off" cancels whatever is currently running. */
  pick(choice: SleepPick): void {
    this.clearInterval();
    this.warned = false;
    this.endedAt = null;
    this.lastChapterIndex = null;

    switch (choice.kind) {
      case "off":
        this.setState(OFF_STATE);
        return;
      case "duration":
        this.setState({
          mode: { kind: "duration", minutes: choice.minutes },
          remaining: choice.minutes * 60,
          fading: false,
        });
        this.startInterval();
        return;
      case "endOfChapter":
        // Remaining stays unknown until the next update() reports the chapter's end.
        this.setState({ mode: { kind: "endOfChapter" }, remaining: null, fading: false });
        return;
      case "chapters":
        this.setState({
          mode: { kind: "chapters", total: choice.count, left: choice.count },
          remaining: null,
          fading: false,
        });
        return;
    }
  }

  stop(): void {
    this.pick({ kind: "off" });
  }

  /**
   * Feed the player's clock in. This module never reads an <audio> element
   * itself, so "end of chapter" and "N chapters" only learn where the cutoff
   * is because the caller reports it here, typically on every timeupdate.
   */
  update(currentTime: number, chapterEndTime: number, chapterIndex: number): void {
    const mode = this.state.mode;
    if (mode.kind === "off" || mode.kind === "duration") return;

    if (mode.kind === "chapters" && this.lastChapterIndex !== null && chapterIndex !== this.lastChapterIndex) {
      const left = mode.left - 1;
      if (left <= 0) {
        this.cutoff();
        return;
      }
      this.setState({ ...this.state, mode: { ...mode, left } });
    }
    this.lastChapterIndex = chapterIndex;

    // Only "end of chapter" and the last chapter of a "chapters" countdown
    // have a deadline worth counting down to; earlier chapters stay unknown.
    const current = this.state.mode;
    const counting = current.kind === "endOfChapter" || (current.kind === "chapters" && current.left === 1);
    if (!counting) return;

    const remaining = Math.max(0, chapterEndTime - currentTime);
    if (remaining <= 0) {
      this.cutoff();
      return;
    }
    this.checkWarning(remaining);
    this.setState({ ...this.state, remaining, fading: remaining <= FADE_SECONDS });
  }

  /**
   * More time. While a countdown is running, it simply pushes the deadline
   * back. Once it has fired, it only works for EXTEND_GRACE_MS: past that
   * window this is a no-op, because the classic "I wasn't asleep yet" is a
   * claim made in the seconds right after the cutoff, not five minutes later.
   */
  extend(minutes: number): void {
    if (this.state.mode.kind !== "off") {
      if (this.state.remaining === null) return; // nothing numeric to push back yet
      const remaining = this.state.remaining + minutes * 60;
      this.warned = false;
      this.setState({ ...this.state, remaining, fading: remaining <= FADE_SECONDS });
      return;
    }
    if (this.endedAt === null || Date.now() - this.endedAt > EXTEND_GRACE_MS) return;
    this.pick({ kind: "duration", minutes });
  }

  /** Clear timers and listeners. Call on unmount: no interval survives it. */
  destroy(): void {
    this.clearInterval();
    this.listeners.clear();
    this.warningListeners.clear();
  }

  private cutoff(): void {
    this.clearInterval();
    this.endedAt = Date.now();
    this.lastChapterIndex = null;
    this.setState(OFF_STATE);
  }

  private startInterval(): void {
    this.intervalId = setInterval(() => {
      if (this.state.mode.kind !== "duration" || this.state.remaining === null) return;
      const remaining = this.state.remaining - 1;
      if (remaining <= 0) {
        this.cutoff();
        return;
      }
      this.checkWarning(remaining);
      this.setState({ ...this.state, remaining, fading: remaining <= FADE_SECONDS });
    }, 1000);
  }

  private clearInterval(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private checkWarning(remaining: number): void {
    if (!this.warned && remaining <= WARNING_SECONDS) {
      this.warned = true;
      for (const listener of this.warningListeners) listener();
    }
  }

  private setState(next: SleepState): void {
    this.state = next;
    for (const listener of this.listeners) listener(next);
  }
}
